import { ChangeDetectionStrategy, Component, inject, ViewChild } from '@angular/core';
import { ApiErrorResponse } from '../../../../core/classes/ApiErrorResponse';
import { BaseModalComponent } from '../../../../core/layout/components/abstracts/base-modal.component';
import { ChipComponent } from '../../../../shared/components/chip/chip.component';
import { ModalComponent } from '../../../../shared/components/modal/modal.component';
import { TranslatePipe } from '../../../../shared/pipes/trans.pipe';
import { CartItemTypes } from '../../../cart/core/enums/CartItemTypes';
import { CartService } from '../../../cart/core/services/CartService';
import { CartStore } from '../../../cart/core/stores/CartStore';
import { ICartItem } from '../../../cart/core/interfaces/ICartItem';
import { IVoucherType } from '../../core/interfaces/IVoucherType';
import { VouchersService } from '../../core/services/VouchersService';
import { VouchersStore } from '../../core/stores/VouchersStore';
import { externalVoucherTicketPickerDecision } from '../../core/utils/externalVoucherTicketPickerDecision';
import { filterTicketsConsumedByExistingExternalVouchers } from '../../core/utils/filterTicketsConsumedByExistingExternalVouchers';
import { isFixedAmountTrackOnlyExternal } from '../../core/utils/fixedAmountTrackOnlyExternal';
import { ExternalVoucherTicketPickerModalComponent } from '../external-voucher-ticket-picker-modal/external-voucher-ticket-picker-modal.component';
import { VoucherAmountPickerModalComponent } from '../voucher-amount-picker-modal/voucher-amount-picker-modal.component';
import { VoucherCodeInputModalComponent } from '../voucher-code-input-modal/voucher-code-input-modal.component';

@Component({
  selector: 'app-external-vouchers-modal',
  standalone: true,
  imports: [
    ModalComponent,
    TranslatePipe,
    ChipComponent,
    VoucherCodeInputModalComponent,
    VoucherAmountPickerModalComponent,
    ExternalVoucherTicketPickerModalComponent
  ],
  templateUrl: './external-vouchers-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExternalVouchersModalComponent extends BaseModalComponent {

  @ViewChild('voucherCodeInputModal') voucherCodeInputModal: VoucherCodeInputModalComponent;
  @ViewChild('voucherAmountPickerModal') voucherAmountPickerModal: VoucherAmountPickerModalComponent;
  @ViewChild('distributorTicketPickerModal') distributorTicketPickerModal: ExternalVoucherTicketPickerModalComponent;

  public readonly vouchersStore = inject(VouchersStore);

  private readonly _cartService = inject(CartService);
  private readonly _cartStore = inject(CartStore);
  private readonly _vouchersService = inject(VouchersService);

  public selectedVoucherType: IVoucherType;

  public override open(val?: null): void {
    this.selectedVoucherType = null;
    super.open(val);
  }

  public get isAcardo(): boolean {
    return this.selectedVoucherType?.flagAcardo === true;
  }

  public get isDistributor(): boolean {
    return this.selectedVoucherType?.flagDistributor === true;
  }

  public get isCodeVisible(): boolean {
    return this.isAcardo ||
           this.selectedVoucherType?.flagGroup ||
           this.selectedVoucherType?.isSeries ||
           this.selectedVoucherType?.flagIdentifiable;
  }

  public get isAmountFieldVisible(): boolean {
    if (!this.selectedVoucherType) {
      return false;
    }

    let bHasAmount = !this.selectedVoucherType.flagInternal &&
      !this.selectedVoucherType.flagValueTicket ||
      this.selectedVoucherType.flagVariable;

    if (this.selectedVoucherType.keyExternalApi) {
      bHasAmount = false;
    }

    return bHasAmount;
  }

  public selectVoucherType(voucherType: IVoucherType): void {
    this.selectedVoucherType = voucherType;

    if (this.isDistributor) {
      this.handleDistributorVoucherValidation();
      return;
    }

    if (isFixedAmountTrackOnlyExternal(voucherType)) {
      const isClassicFixed =
        !voucherType.flagVariable && voucherType.amountMin === voucherType.amountMax;

      if (isClassicFixed) {
        this.applyFixedAmountTrackOnlyExternal();
        return;
      }

      this.close();
      this.voucherAmountPickerModal.open(voucherType);
      return;
    }

    this.close();

    if (this.isCodeVisible) {
      this.voucherCodeInputModal.open(voucherType);
    } else if (this.isAmountFieldVisible) {
      this.voucherAmountPickerModal.open(voucherType);
    }
  }

  public onDistributorTicketSelected(ticket: ICartItem): void {
    this.applyAndAddDistributor(ticket);
  }

  private handleDistributorVoucherValidation(): void {
    const tickets = this._cartStore.cartItemsTickets();
    const externalVouchers = this._cartStore.cartItemsExternalVouchers();
    const availableTickets = filterTicketsConsumedByExistingExternalVouchers(tickets, externalVouchers);
    const decision = externalVoucherTicketPickerDecision(availableTickets);

    if (!decision.canContinueWithoutPicker && decision.reason === 'no-tickets') {
      this.notificationService.pushError(
        'Fehler'.trans('notifications/error.Title'),
        'Add a ticket to the cart before using this voucher.'.trans('vouchers/external-voucher-ticket-picker-modal/no-tickets-error.Message')
      );
      return;
    }

    if (decision.canContinueWithoutPicker) {
      this.applyAndAddDistributor(availableTickets[0]);
      return;
    }

    this.distributorTicketPickerModal.open(availableTickets);
  }

  private applyFixedAmountTrackOnlyExternal(): void {
    this.close();
    const voucherNumber = this.generateSessionVoucherNumber();
    this.addExternalVoucherToCart(voucherNumber, this.selectedVoucherType.amountMin, 0);
  }

  private applyAndAddDistributor(ticket: ICartItem): void {
    const price = Math.abs(ticket.totalPrice);

    this._vouchersService.applyDistributorVoucher(
      this.selectedVoucherType.voucherTypeId,
      price
    ).subscribe({
      next: ({ data }) => {
        const voucherNumber = this.generateSessionVoucherNumber();
        this.addExternalVoucherToCart(voucherNumber, data.value, ticket.showId);
      },
      error: ({ error }: ApiErrorResponse) => this.handleApiError(error)
    });
  }

  private addExternalVoucherToCart(voucherNumber: string, price: number, showId: number): void {
    this._cartService.addItemToCart([
      {
        itemId: this.selectedVoucherType.voucherTypeId,
        itemType: CartItemTypes.ExternalVoucher,
        quantity: 1,
        voucherNumber,
        price,
        showId
      }
    ]).subscribe({
      next: () => this.close(),
      error: ({ error }: ApiErrorResponse) => this.handleApiError(error)
    });
  }

  private generateSessionVoucherNumber(): string {
    const storageKey = 'cinetixx.cash.distributor-voucher-number';
    const currentValue = Number(sessionStorage.getItem(storageKey) ?? '0');
    const nextValue = Number.isFinite(currentValue) && currentValue > 0 ? currentValue + 1 : 1;

    sessionStorage.setItem(storageKey, String(nextValue));

    return `#${String(nextValue)}`;
  }

  private handleApiError(error: { message: string }): void {
    this.notificationService.pushError(
      'Fehler'.trans('notifications/error.Title'),
      error.message.trans(`add-voucher-item-to-cart/notifications/${error.message}.Message`)
    );
  }
}
